import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { CountryDetails } from '@supoui/shared/model/country';
import { DropdownDictionary } from '@supoui/shared/model/dropdownDictionary';
import { SupplierProfile } from '@supoui/shared/model/supplier-profile';
import { AccountService } from '@supoui/core/auth/account.service';
import { UserService } from '@supoui/core/user/user.service';
import { SupoCaseInvoiceService } from '@supoui/core/services/supo-case-invoice.service';
import { RequestUserInformation } from '@supoui/shared/model/request-user-information';
import { AbstractFormGroupComponent } from '../abstract-form-group.component';
import { SendFormSuccessModalComponent } from '../../../new-case/components/send-form-success-modal/send-form-success-modal.component';
import { REDIRECT_TIME } from '@supoui/shared/constants/application.constants';
import { Subscription, timer } from 'rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';

interface mobileInput {
  code: string;
  prefix: string;
  number: number;
}

@Component({
    selector: 'app-send-form-modal',
    templateUrl: 'send-form-modal.component.html',
    styleUrls: ['./send-form-modal.component.scss'],
    standalone: false
})
@UntilDestroy({ checkProperties: true })
export class SendFormModalComponent extends AbstractFormGroupComponent implements OnInit {
  @Input() public firstName: string;
  @Input() public mobile: mobileInput;
  @Input() public onBehalfOf: string;
  @Input() public refNo: string;

  supplierSelect: DropdownDictionary[] = [];
  private supplierProfiles: SupplierProfile[] = [];

  private redirecTimerSubscr: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private router: Router,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private accountService: AccountService,
    private userService: UserService,
    private supoCaseInvoiceService: SupoCaseInvoiceService,
    private modalService: NgbModal
  ) {
    super();
  }

  ngOnInit(): void {
    this.fg = this.fb.group({
      firstName: [this.firstName, [Validators.required]],
      mobilePhoneCountryCode: [this.mobile?.code, [Validators.required]],
      mobilePhonePrefix: [this.mobile?.prefix, [Validators.required]],
      mobilePhone: [this.mobile?.number, [Validators.required, Validators.pattern('^[0-9]*$')]],
      onBehalfOf: [this.onBehalfOf, [Validators.required]],
      refNo: [this.refNo]
    });

    this.supplierProfiles = [];
    const isAdmin = this.accountService.isAdmin();
    this.userService.supplierProfiles(true).subscribe(allSuppliers => {
      this.supplierProfiles = allSuppliers;
      this.supplierSelect = allSuppliers.map(
        s => new DropdownDictionary(s.supplierProfileCode, s.supplierProfileName + ' - ' + s.supplierProfileCode)
      );
      if (!isAdmin) {
        if (this.supplierProfiles.length === 1) {
          this.fg.controls.onBehalfOf.disable();
          this.fg.controls.onBehalfOf.patchValue(this.supplierProfiles[0].supplierProfileCode);
        }
      }
    });
  }

  submitForm() {
    if (this.fg.valid) {
      const values = this.fg.getRawValue();

      const request = new RequestUserInformation({
        firstName: values.firstName,
        surname: values.surname,
        mobilePhoneCountryCode: values.mobilePhoneCountryCode,
        mobilePhonePrefix: values.mobilePhonePrefix,
        mobilePhoneNumber: values.mobilePhone,
        supplierProfileCode: values.onBehalfOf,
        refNo: values.refNo
      });

      this.supoCaseInvoiceService.sendInformationRequestToEnduser(request).subscribe(
        () => this.sendInformationSuccess(),
        err => (this.error = err)
      );
    } else {
      this.validateAllFormFields(this.fg);
    }
  }

  sendInformationSuccess(): void {
    const modalRef = this.modalService.open(SendFormSuccessModalComponent, {
      size: 'md',
      centered: true,
      scrollable: false
    });
    modalRef.result.then(result => {
      this.redirectToHome();
    });
    this.redirecTimerSubscr = timer(REDIRECT_TIME).subscribe(() => {
      modalRef.close();
      this.redirectToHome();
    });
  }

  closeModalEvent(result?: boolean): void {
    this.activeModal.close(result);
  }

  endUserMobilePhoneCountryChanged(country: CountryDetails): void {
    this.fg.controls.mobilePhonePrefix.patchValue(country.phonePrefix);
  }

  private redirectToHome() {
    this.closeModalEvent(true);
    this.router.navigate(['/', 'home']);
  }
}
